<template>
  <div class="leaflet-map-view">
    <div v-if="pickable && !readOnly" class="leaflet-map-view__search">
      <FormInput
        v-model="searchQuery"
        :placeholder="$t('leafletMapView.searchPlaceholder')"
        :loading="searching"
        @input="debouncedSearch"
      ></FormInput>
      <ul
        v-if="searchResults.length > 0"
        class="leaflet-map-view__search-results"
      >
        <li
          v-for="(result, index) in searchResults"
          :key="index"
          class="leaflet-map-view__search-result"
          @click="selectSearchResult(result)"
        >
          {{ result.display_name }}
        </li>
      </ul>
      <Alert v-if="searchError" type="error" class="leaflet-map-view__error">
        {{ $t('leafletMapView.searchError') }}
      </Alert>
    </div>
    <div ref="mapEl" class="leaflet-map-view__map"></div>
  </div>
</template>

<script>
import debounce from 'lodash/debounce'
// Leaflet's default marker icon paths are asset URLs meant to be resolved
// relative to a CSS file on a static file server -- broken by design under
// any bundler, since the bundler never sees those string paths to rewrite
// them. Re-pointed at the bundler-resolved copies instead, the standard fix
// for this well-known Leaflet+bundler issue.
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'

import GeocodingService from '@baserow/modules/core/services/geocoding'
import { notifyIf } from '@baserow/modules/core/utils/error'

const DEFAULT_CENTER = [51.505, -0.09]
const DEFAULT_ZOOM = 13
const SINGLE_MARKER_ZOOM = 15

/**
 * A thin wrapper around Leaflet, shared by the GeoData field's map picker
 * and the Map view's canvas so both render/behave identically and only one
 * place has to manage the map's lifecycle, marker updates and (optional)
 * address search.
 *
 * Two modes, chosen by the caller:
 * - `pickable`: a single, draggable marker for picking one location (the
 *   GeoData cell editor). Emits `picked` with `{ lat, lng }` on click or
 *   drag. Shows the address search box.
 * - not `pickable`: renders whatever `markers` the caller passes (the Map
 *   view canvas, one marker per row), read-only, no search box.
 */
export default {
  name: 'LeafletMapView',
  props: {
    pickable: {
      type: Boolean,
      required: false,
      default: false,
    },
    // When `pickable` but `readOnly`, the map still opens and centers on
    // `value` -- viewing a location must never depend on write permission --
    // but the search box and click/drag-to-pick handlers are suppressed, the
    // same split `GridViewFieldFile`'s lightbox draws between opening a file
    // (always allowed) and uploading a new one (gated).
    readOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
    // Only used when `pickable`: the single marker's starting position.
    value: {
      type: Object,
      required: false,
      default: null,
      // { lat: Number, lng: Number }
    },
    // Only used when not `pickable`: one marker per entry.
    markers: {
      type: Array,
      required: false,
      default: () => [],
      // [{ id, lat, lng, popupHtml }]
    },
  },
  emits: ['picked', 'marker-click'],
  data() {
    return {
      searchQuery: '',
      searchResults: [],
      searching: false,
      searchError: false,
    }
  },
  watch: {
    value: {
      handler(newValue) {
        this.setPickedMarker(newValue)
      },
      deep: true,
    },
    markers: {
      handler(newMarkers) {
        this.renderMarkers(newMarkers)
      },
      deep: true,
    },
  },
  created() {
    this.debouncedSearch = debounce(this.search, 400)
  },
  async mounted() {
    // Leaflet reads `window` the moment it is evaluated, not just when it is
    // used -- a static top-level `import` crashed Nuxt's SSR render on every
    // page this component's *file* was reachable from, not just pages that
    // actually rendered a map. `mounted` only ever runs client-side, so a
    // dynamic import here is the only import of Leaflet's module body that
    // happens, and it happens after `window` already exists.
    const L = (await import('leaflet')).default
    this.L = L

    delete L.Icon.Default.prototype._getIconUrl
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: markerIcon2x,
      iconUrl: markerIcon,
      shadowUrl: markerShadow,
    })

    this.map = L.map(this.$refs.mapEl).setView(DEFAULT_CENTER, DEFAULT_ZOOM)
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(this.map)

    // Leaflet measures the container once, when the map is created, and then
    // trusts that number until told otherwise. `mounted` runs before the
    // browser has necessarily laid this element out -- and the await above
    // makes that more likely, not less -- so the map can be created against a
    // 0x0 box. It then requests no tiles and renders nothing, silently: no
    // error, no console warning, just white. Watching the element and
    // re-measuring on the first real size is what turns that back into a map.
    this.resizeObserver = new ResizeObserver(() => this.map?.invalidateSize())
    this.resizeObserver.observe(this.$refs.mapEl)

    if (this.pickable) {
      if (!this.readOnly) {
        this.map.on('click', (event) => this.pickLocation(event.latlng))
      }
      this.setPickedMarker(this.value)
    } else {
      this.markerLayer = L.layerGroup().addTo(this.map)
      this.renderMarkers(this.markers)
    }
  },
  beforeUnmount() {
    this.resizeObserver?.disconnect()
    this.map?.remove()
  },
  methods: {
    pickLocation(latlng) {
      this.setPickedMarker(latlng)
      this.$emit('picked', { lat: latlng.lat, lng: latlng.lng })
    },
    setPickedMarker(position) {
      // The `value`/`markers` watchers can fire before Leaflet's dynamic
      // import above resolves (a prop can change in the same tick the
      // component mounts); no-op until the map actually exists rather than
      // throwing on a still-undefined `this.L`/`this.map`.
      if (!this.map) {
        return
      }
      const L = this.L
      if (!position || position.lat == null || position.lng == null) {
        this.pickedMarker?.remove()
        this.pickedMarker = null
        return
      }
      const latlng = L.latLng(position.lat, position.lng)
      if (this.pickedMarker) {
        this.pickedMarker.setLatLng(latlng)
      } else {
        this.pickedMarker = L.marker(latlng, {
          draggable: this.pickable && !this.readOnly,
        }).addTo(this.map)
        if (this.pickable && !this.readOnly) {
          this.pickedMarker.on('dragend', () =>
            this.pickLocation(this.pickedMarker.getLatLng())
          )
        }
      }
      this.map.setView(latlng, SINGLE_MARKER_ZOOM)
    },
    renderMarkers(markers) {
      if (!this.markerLayer) {
        return
      }
      const L = this.L
      this.markerLayer.clearLayers()
      const points = []
      markers.forEach((marker) => {
        if (marker.lat == null || marker.lng == null) {
          return
        }
        const latlng = L.latLng(marker.lat, marker.lng)
        points.push(latlng)
        const leafletMarker = L.marker(latlng)
        if (marker.popupHtml) {
          leafletMarker.bindPopup(marker.popupHtml)
        }
        leafletMarker.on('click', () => this.$emit('marker-click', marker.id))
        leafletMarker.addTo(this.markerLayer)
      })
      if (points.length > 0) {
        this.map.fitBounds(L.latLngBounds(points), {
          maxZoom: 15,
          padding: [30, 30],
        })
      }
    },
    async search(query) {
      if (!query || query.trim() === '') {
        this.searchResults = []
        return
      }
      this.searching = true
      this.searchError = false
      try {
        const { data } = await GeocodingService(this.$client).search(query)
        this.searchResults = data
      } catch (error) {
        this.searchResults = []
        this.searchError = true
        notifyIf(error, 'geocoding')
      } finally {
        this.searching = false
      }
    },
    selectSearchResult(result) {
      this.searchResults = []
      this.searchQuery = result.display_name
      this.pickLocation({ lat: Number(result.lat), lng: Number(result.lng) })
    },
  },
}
</script>
